<template>
	<div class="virtual-list-container">
		<!-- 虚拟滚动容器 -->
		<div
			ref="containerRef"
			class="virtual-scroll-container"
			:style="{ height: containerHeight + 'px' }"
			@scroll="handleScroll"
		>
			<!-- 总高度撑开容器 -->
			<div class="scroll-height" :style="{ height: totalHeight + 'px' }">
				<!-- 可视区域渲染项 -->
				<div
					class="visible-items"
					:style="{ transform: `translateY(${offsetY}px)` }"
				>
					<div
						v-for="item in visibleItems"
						:key="getItemKey(item.data, item.index)"
						:class="['virtual-item', `virtual-item-${item.index}`]"
						:style="{ height: item.height + 'px' }"
					>
						<!-- 插槽内容 -->
						<slot
							:item="item.data"
							:index="item.index"
							:isVisible="true"
						></slot>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { throttle } from '@/utils/api';

// Props
const props = defineProps({
	// 列表数据
	items: {
		type: Array,
		default: () => []
	},
	// 每项固定高度（px）
	itemHeight: {
		type: Number,
		default: 60
	},
	// 容器高度（px）
	containerHeight: {
		type: Number,
		default: 400
	},
	// 缓冲区大小（上下各渲染多少个额外项）
	bufferSize: {
		type: Number,
		default: 5
	},
	// 获取项的唯一key的函数
	getItemKey: {
		type: Function,
		default: (item, index) => index
	}
});

// Emits
const emit = defineEmits(['scroll']);

// Refs
const containerRef = ref(null);

// 响应式数据
const scrollTop = ref(0);

// 高度缓存系统
const heightCache = new Map(); // 缓存每个item的高度
const positionCache = new Map(); // 缓存每个位置的累计高度
let totalHeightCache = 0; // 缓存总高度
let cacheBuilt = false; // 缓存是否已构建

// 获取单个item的实际高度（带缓存）
const getItemHeight = (item, index) => {
	const cacheKey = `${index}-${item.monthValue}-${item.isFirstRowOfMonth}`;
	
	if (heightCache.has(cacheKey)) {
		return heightCache.get(cacheKey);
	}
	
	// 计算高度
	let height = props.itemHeight;
	if (item.isFirstRowOfMonth && item.monthValue !== 1) {
		height += 24;
	}
	
	// 缓存结果
	heightCache.set(cacheKey, height);
	return height;
};

// 构建位置缓存（累计高度数组）
const buildPositionCache = () => {
	if (cacheBuilt && positionCache.size === props.items.length && totalHeightCache > 0) {
		return; // 缓存已存在且有效
	}
	
	positionCache.clear();
	let accumulatedHeight = 0;
	
	for (let i = 0; i < props.items.length; i++) {
		positionCache.set(i, accumulatedHeight);
		accumulatedHeight += getItemHeight(props.items[i], i);
	}
	
	totalHeightCache = accumulatedHeight;
	cacheBuilt = true;
};

// 获取到指定索引的累计高度（O(1)复杂度）
const getAccumulatedHeight = (endIndex) => {
	buildPositionCache();
	return positionCache.get(endIndex) || 0;
};

// 二分查找startIndex（O(log n)复杂度）
const findStartIndex = (targetScrollTop) => {
	buildPositionCache();
	
	let left = 0;
	let right = props.items.length - 1;
	let result = 0;
	
	while (left <= right) {
		const mid = Math.floor((left + right) / 2);
		const midPosition = positionCache.get(mid) || 0;
		
		if (midPosition <= targetScrollTop) {
			result = mid;
			left = mid + 1;
		} else {
			right = mid - 1;
		}
	}
	
	return result;
};

// 清理缓存
const clearCache = () => {
	heightCache.clear();
	positionCache.clear();
	totalHeightCache = 0;
	cacheBuilt = false;
};

// 重新初始化虚拟滚动组件
const reinitialize = () => {
	// 清理缓存
	clearCache();
	// 重新构建位置缓存
	buildPositionCache();
	// 如果容器存在，保持当前滚动位置并更新可视区域
	if (containerRef.value) {
		// 保持当前滚动位置
		const currentScrollTop = containerRef.value.scrollTop;
		scrollTop.value = currentScrollTop;
		// 触发一次滚动事件以更新可视区域
		handleScroll({ target: containerRef.value });
	}
};

// 监听数据变化，清理缓存
watch(() => props.items, () => {
	clearCache();
}, { deep: true });

// 监听 itemHeight 变化，清理缓存
watch(() => props.itemHeight, () => {
	clearCache();
});

// 计算属性
const totalHeight = computed(() => {
	buildPositionCache();
	return totalHeightCache;
});

const startIndex = computed(() => {
	// 使用二分查找快速定位startIndex（O(log n)复杂度）
	const targetIndex = findStartIndex(scrollTop.value);
	
	// 智能缓冲区计算：确保不超出数据范围
	const availableBuffer = Math.min(props.bufferSize, targetIndex);
	return Math.max(0, targetIndex - availableBuffer);
});

const endIndex = computed(() => {
	// 计算可视区域需要的高度
	const viewportBottom = scrollTop.value + props.containerHeight;
	buildPositionCache();
	
	// 从startIndex开始查找endIndex
	let targetEndIndex = startIndex.value;
	
	// 优化查找：从startIndex开始，而不是从0开始
	for (let i = startIndex.value; i < props.items.length; i++) {
		const itemPosition = positionCache.get(i) || 0;
		const itemHeight = getItemHeight(props.items[i], i);
		
		if (itemPosition + itemHeight >= viewportBottom) {
			targetEndIndex = i;
			break;
		}
		targetEndIndex = i;
	}
	
	// 智能缓冲区计算：确保不超出数据范围
	const remainingItems = props.items.length - 1 - targetEndIndex;
	const availableBuffer = Math.min(props.bufferSize, remainingItems);
	
	return Math.min(props.items.length - 1, targetEndIndex + availableBuffer);
});

const visibleItems = computed(() => {
	const items = [];
	for (let i = startIndex.value; i <= endIndex.value; i++) {
		if (props.items[i]) {
			items.push({
				index: i,
				data: props.items[i],
				height: getItemHeight(props.items[i], i) // 使用缓存版本
			});
		}
	}
	
	

	
	return items;
});

const offsetY = computed(() => {
	return startIndex.value > 0 ? getAccumulatedHeight(startIndex.value) : 0;
});

// 获取实际使用的起始缓冲区大小
const getActualStartBuffer = () => {
	// 找到真实的可视区域起始索引
	let accumulatedHeight = 0;
	let realStartIndex = 0;
	
	for (let i = 0; i < props.items.length; i++) {
		const itemHeight = getItemHeight(props.items[i]);
		if (accumulatedHeight + itemHeight > scrollTop.value) {
			realStartIndex = i;
			break;
		}
		accumulatedHeight += itemHeight;
		realStartIndex = i + 1;
	}
	
	// 实际使用的起始缓冲区 = 当前startIndex到真实startIndex的距离
	return Math.max(0, realStartIndex - startIndex.value);
};

// 获取实际使用的结束缓冲区大小
const getActualEndBuffer = () => {
	// 找到真实的可视区域结束索引
	const viewportBottom = scrollTop.value + props.containerHeight;
	let accumulatedHeight = 0;
	let realEndIndex = 0;
	
	for (let i = 0; i < props.items.length; i++) {
		accumulatedHeight += getItemHeight(props.items[i]);
		if (accumulatedHeight >= viewportBottom) {
			realEndIndex = i;
			break;
		}
		realEndIndex = i;
	}
	
	// 实际使用的结束缓冲区 = 当前endIndex到真实endIndex的距离
	return Math.max(0, endIndex.value - realEndIndex);
};

// 方法
const handleScroll = throttle((event) => {
	const { scrollTop: newScrollTop } = event.target;
	scrollTop.value = newScrollTop;
	// 计算实际使用的缓冲区大小
	const actualStartBuffer = getActualStartBuffer();
	const actualEndBuffer = getActualEndBuffer();
	

	// 发出滚动事件
	emit('scroll', {
		scrollTop: newScrollTop,
		startIndex: startIndex.value + actualStartBuffer, // 真实的可视区域起始索引
		endIndex: endIndex.value - actualEndBuffer,       // 真实的可视区域结束索引
		visibleItems: visibleItems.value
	});
},10);

const scrollToIndex = (index) => {
	if (!containerRef.value) return;
	
	// 确保索引在有效范围内
	let validIndex = Math.max(0, Math.min(index, props.items.length - 1));
	console.log("🚀 ~ scrollToIndex ~ validIndex:", validIndex)
	
	// 检查是否有足够的数据支撑滚动到顶部
	const totalContentHeight = totalHeight.value;
	const maxScrollTop = Math.max(0, totalContentHeight - props.containerHeight);
	
	// 计算目标索引的滚动位置
	let targetScrollTop = validIndex > 0 ? getAccumulatedHeight(validIndex) : 0;
	
	// 如果目标滚动位置超过了最大可滚动位置，滚动到底部
	if (targetScrollTop > maxScrollTop) {
		targetScrollTop = maxScrollTop;
		console.log("🚀 ~ scrollToIndex ~ 数据不足，滚动到底部:", maxScrollTop);
	}
	
	console.log("🚀 ~ scrollToIndex ~ validIndex:", validIndex, "targetScrollTop:", targetScrollTop, "maxScrollTop:", maxScrollTop);
	
	containerRef.value.scrollTop = targetScrollTop;
	scrollTop.value = targetScrollTop;
};



const scrollToBottom = () => {
	if (!containerRef.value) return;
	
	const maxScrollTop = Math.max(0, totalHeight.value - props.containerHeight);
	containerRef.value.scrollTop = maxScrollTop;
	scrollTop.value = maxScrollTop;
};

// 平滑滚动到指定位置
const smoothScrollTo = (targetIndex, duration = 300) => {
	if (!containerRef.value) return;
	
	const startScrollTop = scrollTop.value;
	let validIndex = Math.max(0, Math.min(targetIndex, props.items.length - 1));
	
	// 检查是否有足够的数据支撑滚动到顶部
	const totalContentHeight = totalHeight.value;
	const maxScrollTop = Math.max(0, totalContentHeight - props.containerHeight);
	
	// 计算目标滚动位置
	let targetScrollTop = validIndex > 0 ? getAccumulatedHeight(validIndex) : 0;
	
	// 如果目标滚动位置超过了最大可滚动位置，滚动到底部
	if (targetScrollTop > maxScrollTop) {
		targetScrollTop = maxScrollTop;
	}
	
	const startTime = Date.now();
	
	const animateScroll = () => {
		const elapsed = Date.now() - startTime;
		const progress = Math.min(elapsed / duration, 1);
		
		// 使用缓动函数
		const easeOutCubic = 1 - Math.pow(1 - progress, 3);
		const currentScrollTop = startScrollTop + (targetScrollTop - startScrollTop) * easeOutCubic;
		
		containerRef.value.scrollTop = currentScrollTop;
		scrollTop.value = currentScrollTop;
		
		if (progress < 1) {
			requestAnimationFrame(animateScroll);
		}
	};
	
	requestAnimationFrame(animateScroll);
};

// 获取当前可视区域的项
const getVisibleItems = () => {
	return visibleItems.value;
};

// 窗口大小变化处理函数（使用节流优化性能）
const handleResize = throttle(() => {
	reinitialize();
}, 150);

// 生命周期
onMounted(() => {
	// 组件挂载后的初始化
	// 监听窗口大小变化
	window.addEventListener('resize', handleResize);
});

// 组件卸载时移除监听器
onUnmounted(() => {
	window.removeEventListener('resize', handleResize);
});

// 暴露给父组件的方法
defineExpose({
	scrollToIndex,
	scrollToBottom,
	smoothScrollTo,
	getVisibleItems
});
</script>

<style lang="less" scoped>
.virtual-list-container {
	position: relative;
	width: 100%;
}

.virtual-scroll-container {
	position: relative;
	overflow: auto;
	
	&::-webkit-scrollbar {
		width: 0;
	}
}

.scroll-height {
	position: relative;
}

.visible-items {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
}

.virtual-item {
	position: relative;
	box-sizing: border-box;
	overflow: hidden;
}
</style>
